import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
  Button,
  IconButton,
  MenuItem,
  Modal,
  Select,
  Typography,
} from '@material-ui/core';
import { Add, DeleteForeverOutlined } from '@material-ui/icons';
import CustomTable from '../components/CustomTable';
import ActionButton from '../../../components/ActionButton';
import TipologiaStrumentazioneModal, {
  labelTipologia,
} from '../components/TipologiaStrumentazioneModal';
import {
  getGestioneOperativaLocali,
  getGestioneOperativaProcedure,
  getGestioneOperativaProceduraById,
  getGestioneOperativaStrumentazione,
  updateGestioneOperativaStrumentazione,
} from '../../../utils/api/aggiornamentoGestioneOperativaApi';
import CustomSnackbar from '../../../components/CustomSnackbar';
import EditableCell from '../components/EditableCell';
import { reportApiError, reportError } from '../../../utils/reportApiError';

const useStyles = makeStyles((theme) => ({
  centeredCellContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  modal: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(2),
    textAlign: 'center',
    backgroundColor: 'white',
    color: 'black',
    padding: '20px 10px',
    boxShadow: theme.shadows[6],
    borderRadius: '6px',
    overflowY: 'auto',
    width: '90%',
    maxWidth: '1020px',
  },
  modalTitle: {
    marginTop: theme.spacing(1),
  },
  spanTitle: {
    backgroundColor: theme.palette.grey[300],
    padding: '4px 8px',
    borderRadius: '8px',
    fontWeight: 600,
  },
  upperButtonWrapper: {
    display: 'flex',
    justifyContent: 'right',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '90%',
    margin: '10px 0',
  },
  addButton: {
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  tableWrapper: {
    width: '90%',
  },
  buttonWrapper: {
    width: '90%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  selectPlaceholder: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
}));

const SelezioneAttrezzatureModal = ({
  gestioneOperativaId,
  onClose,
  open,
  onSave,
  onLocaliRefetch,
  selectedLocale,
  setSelectedLocale,
}) => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);

  const [openAddTipologiaModal, setOpenAddTipologiaModal] = useState(false);
  const [openRemoveTipologiaModal, setOpenRemoveTipologiaModal] =
    useState(false);
  const [tipologiaList, setTipologiaList] = useState([]);
  const [localiList, setLocaliList] = useState([]);
  const [controlliTipologiaIds, setControlliTipologiaIds] = useState([]);
  const [loadingControlliCheck, setLoadingControlliCheck] = useState(false);
  const [newTipologia, setNewTipologia] = useState('');
  const [selectedTipologiaToRemove, setSelectedTipologiaToRemove] =
    useState('');

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
  });

  const handleOpenSnackbar = (message) => {
    setSnackbar({ open: true, message });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '' });
  };

  const fetchStrumentazioni = async () => {
    if (!gestioneOperativaId) return;

    setLoading(true);
    try {
      const response =
        await getGestioneOperativaStrumentazione(gestioneOperativaId);
      setTipologiaList(response.strumentazioni);
    } catch (error) {
      reportApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocali = async () => {
    if (!gestioneOperativaId) return null;

    try {
      const response = await getGestioneOperativaLocali(gestioneOperativaId);
      setLocaliList(response.locali || []);
      return response;
    } catch (error) {
      reportApiError(error);
      return null;
    }
  };

  const fetchControlliTipologiaIds = async () => {
    if (!gestioneOperativaId) return;

    setLoadingControlliCheck(true);
    setControlliTipologiaIds([]);

    try {
      const proceduraResponse =
        await getGestioneOperativaProcedure(gestioneOperativaId);
      const procedure = proceduraResponse.procedureHaccp || [];

      const dettagli = await Promise.all(
        procedure.map((procedura) =>
          getGestioneOperativaProceduraById(gestioneOperativaId, procedura.id),
        ),
      );

      const tipologiaIds = dettagli
        .flatMap(
          (dettaglio) =>
            dettaglio.procedureHaccp?.[0]?.controlliConformita || [],
        )
        .map((controllo) => controllo.tipologiaStrumentazioneId)
        .filter((id) => id != null)
        .map((id) => String(id));

      setControlliTipologiaIds([...new Set(tipologiaIds)]);
    } catch (error) {
      // Silent on purpose: this check only decides whether to warn about the
      // controls, and failing to answer must not block the removal.
    } finally {
      setLoadingControlliCheck(false);
    }
  };

  const refetchLocale = async (removedTipologiaId = null) => {
    if (!gestioneOperativaId || !selectedLocale?.id) return;

    const isLinkedToRemoved = (attrezzatura) =>
      removedTipologiaId != null &&
      attrezzatura.tipologiaStrumentazioneId === removedTipologiaId;

    const response = await fetchLocali();
    if (!response) return;

    const apiLocale = response.locali?.find(
      (locale) => locale.id === selectedLocale.id,
    );

    if (apiLocale) {
      const draftAttrezzature = (selectedLocale.attrezzature || []).filter(
        (attrezzatura) =>
          attrezzatura.id == null && !isLinkedToRemoved(attrezzatura),
      );
      setSelectedLocale({
        ...apiLocale,
        attrezzature: [...(apiLocale.attrezzature || []), ...draftAttrezzature],
      });
    } else {
      setSelectedLocale((prev) => ({
        ...prev,
        attrezzature: (prev.attrezzature || []).filter(
          (attrezzatura) => !isLinkedToRemoved(attrezzatura),
        ),
      }));
    }
  };

  useEffect(() => {
    fetchStrumentazioni();
    fetchLocali();
  }, [gestioneOperativaId]);

  const handleNewRow = () => {
    if (!selectedLocale) return;

    // Inizializzare l'array delle attrezzature se non esiste
    selectedLocale = {
      ...selectedLocale,
      attrezzature: selectedLocale.attrezzature || [],
    };

    const newAttrezzatura = {
      id: null,
      idGestioneOperativa: gestioneOperativaId,
      tipologiaStrumentazioneId: null,
      nome: 'Nuova attrezzatura',
      quantita: 1,
      indiceOrdinamento: selectedLocale?.attrezzature?.length || 0,
    };
    setSelectedLocale((prev) => ({
      ...prev,
      attrezzature: [...(prev.attrezzature || []), newAttrezzatura],
    }));
  };

  const updateAttrezzatureLocale = (id, rowIndex) => {
    setSelectedLocale((prev) => {
      const attrezzature = prev.attrezzature.map((attrezzatura, index) =>
        index === rowIndex
          ? { ...attrezzatura, tipologiaStrumentazioneId: id }
          : attrezzatura,
      );
      return {
        ...prev,
        attrezzature,
      };
    });
  };

  const handleBlur = (field, localValue, rowIndex) => {
    setSelectedLocale((prev) => {
      const updatedAttrezzature = prev.attrezzature.map(
        (attrezzatura, index) =>
          index === rowIndex
            ? { ...attrezzatura, [field]: localValue }
            : attrezzatura,
      );
      return {
        ...prev,
        attrezzature: updatedAttrezzature,
      };
    });
  };

  const deleteRow = (rowIndex) => {
    setSelectedLocale((prev) => {
      const updatedAttrezzature = prev.attrezzature.filter(
        (_attrezzatura, attrezzaturaIndex) => attrezzaturaIndex !== rowIndex,
      );
      return {
        ...prev,
        attrezzature: updatedAttrezzature,
      };
    });
  };

  const columns = [
    {
      Header: 'Tipologia',
      id: 'tipologia',
      minWidth: 210,
      accessor: (d) => d,
      Cell: (props) => {
        const tipologiaId = props.row.tipologia.tipologiaStrumentazioneId;
        const isValidValue = tipologiaList.some(
          (item) => item.id === tipologiaId,
        );
        const selectedValue = isValidValue ? tipologiaId : '';

        return (
          <div className={classes.centeredCellContent}>
            <Select
              variant="outlined"
              displayEmpty
              value={selectedValue}
              style={{ paddingTop: 0, width: '100%' }}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <span className={classes.selectPlaceholder}>
                      Seleziona tipologia
                    </span>
                  );
                }
                const tipologia = tipologiaList.find(
                  (item) => item.id === selected,
                );
                return tipologia ? labelTipologia(tipologia.nome) : selected;
              }}
            >
              <MenuItem
                onClick={() => setOpenAddTipologiaModal(true)}
                value={'Aggiungi Tipologia'}
                style={{ fontStyle: 'italic' }}
              >
                Aggiungi Tipologia
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setOpenRemoveTipologiaModal(true);
                  setSelectedTipologiaToRemove('');
                  fetchControlliTipologiaIds();
                }}
                value={'Rimuovi Tipologia'}
                style={{ fontStyle: 'italic' }}
              >
                Rimuovi Tipologia
              </MenuItem>
              {tipologiaList.map((item) => (
                <MenuItem
                  value={item.id}
                  key={item.id}
                  onClick={() =>
                    updateAttrezzatureLocale(item.id, props.row._index)
                  }
                >
                  {labelTipologia(item.nome)}
                </MenuItem>
              ))}
            </Select>
          </div>
        );
      },
    },
    {
      Header: 'Attrezzatura',
      id: 'attrezzatura',
      minWidth: 240,
      accessor: (data) => data.nome,
      Cell: (props) => (
        <div className={classes.centeredCellContent}>
          <EditableCell
            value={props.row.attrezzatura}
            onChange={(newValue) =>
              handleBlur('nome', newValue, props.row._index)
            }
          />
        </div>
      ),
    },

    {
      Header: 'Elimina',
      id: 'Elimina',
      width: 120,
      accessor: (data) => data,
      Cell: (props) => (
        <div className={classes.centeredCellContent}>
          <IconButton onClick={() => deleteRow(props.row._index)}>
            <DeleteForeverOutlined color="primary" />
          </IconButton>
        </div>
      ),
    },
  ];

  const onAddTipologiaModalClose = () => {
    setOpenAddTipologiaModal(false);
    setNewTipologia('');
  };

  const onRemoveTipologiaModalClose = () => {
    setOpenRemoveTipologiaModal(false);
    setLoadingControlliCheck(false);
    setControlliTipologiaIds([]);
  };

  const handleInputChangeNewTipologia = (e) => {
    setNewTipologia(e.target.value);
  };

  const handleInputChangeRemoveTipologia = (e) => {
    setSelectedTipologiaToRemove(e.target.value);
  };

  const handleNewTipologia = (nome) => {
    const normalizeNewTipologia = nome.trim().toLocaleLowerCase();

    const alreadyExists = tipologiaList.some(
      (type) =>
        (type.nome ?? '').trim().toLocaleLowerCase() === normalizeNewTipologia,
    );

    if (alreadyExists) {
      reportError(`La tipologia di strumentazione ${nome} è già presente.`);
      return;
    }

    const newTipologiaObject = {
      idGestioneOperativa: gestioneOperativaId,
      strumentazioni: [
        ...tipologiaList,
        {
          id: null,
          nome: nome,
          gestioneOperativaId: gestioneOperativaId,
        },
      ],
    };
    updateGestioneOperativaStrumentazione(
      gestioneOperativaId,
      newTipologiaObject,
    )
      .then(() => {
        fetchStrumentazioni();
        onAddTipologiaModalClose();
      })
      .catch((error) => {
        if (error.status === 409) {
          reportError(`Tipologia ${nome} già presente`);
        } else {
          reportApiError(error);
        }
      });
  };

  const handleRemoveTipologia = (removedTipologiaId) => {
    const newTipologiaList = tipologiaList.filter(
      (tipologia) => tipologia.id !== removedTipologiaId,
    );
    const newTipologiaObject = {
      idGestioneOperativa: gestioneOperativaId,
      strumentazioni: [...newTipologiaList],
    };

    updateGestioneOperativaStrumentazione(
      gestioneOperativaId,
      newTipologiaObject,
    )
      .then(() => {
        fetchStrumentazioni();
        refetchLocale(removedTipologiaId);
        onLocaliRefetch?.(removedTipologiaId);
        onRemoveTipologiaModalClose();
      })
      .catch((error) => {
        reportApiError(error);
      });
  };

  const handleSave = () => {
    const hasInvalidAttrezzature = selectedLocale.attrezzature.some(
      (attrezzatura) => !attrezzatura.tipologiaStrumentazioneId,
    );

    if (hasInvalidAttrezzature) {
      reportError('Selezionare una tipologia per ogni attrezzatura');
      return;
    }

    onSave(selectedLocale);
    handleOpenSnackbar('Attrezzature salvate con successo!');
  };

  const hasAttrezzatureAssociate =
    !!selectedTipologiaToRemove &&
    [
      ...localiList
        .filter((locale) => locale.id !== selectedLocale?.id)
        .flatMap((locale) => locale.attrezzature || []),
      ...(selectedLocale?.attrezzature || []),
    ].some(
      (attrezzatura) =>
        attrezzatura.tipologiaStrumentazioneId === selectedTipologiaToRemove,
    );

  const hasControlliAssociati =
    !loadingControlliCheck &&
    !!selectedTipologiaToRemove &&
    controlliTipologiaIds.includes(String(selectedTipologiaToRemove));

  let removeTipologiaWarning = '';
  if (hasAttrezzatureAssociate && hasControlliAssociati) {
    removeTipologiaWarning =
      'Attenzione: le attrezzature e i controlli di conformità associati a questa tipologia verranno cancellati.';
  } else if (hasAttrezzatureAssociate) {
    removeTipologiaWarning =
      'Attenzione: le attrezzature associate a questa tipologia verranno cancellate.';
  } else if (hasControlliAssociati) {
    removeTipologiaWarning =
      'Attenzione: i controlli di conformità associati a questa tipologia verranno cancellati.';
  }

  return (
    <>
      <Modal open={open} onClose={onClose} className={classes.modal}>
        <div className={classes.modalContainer}>
          <Typography variant="h5" className={classes.modalTitle}>
            Elenco attrezzature del locale{' '}
            <span className={classes.spanTitle}>{selectedLocale.nome}</span>
          </Typography>
          <div className={classes.upperButtonWrapper}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={handleNewRow}
              title="Aggiungi attrezzatura"
              className={classes.addButton}
            >
              Attrezzatura
            </Button>
          </div>
          <div className={classes.tableWrapper}>
            <CustomTable
              columns={columns}
              records={selectedLocale.attrezzature || []}
              defaultPageSize={5}
              filterable={false}
              resizable={true}
              sortable={true}
              loading={loading}
            />
          </div>
          <div className={classes.buttonWrapper}>
            <ActionButton
              disabled={false}
              grayVersion={false}
              secondaryButton
              label="Chiudi"
              onClick={onClose}
            />
            <ActionButton
              // disabled={isButtonRequestPressed}
              grayVersion={false}
              label="Salva"
              onClick={handleSave}
            />
          </div>
        </div>
      </Modal>
      <CustomSnackbar
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        // message="Attrezzature salvate con successo"
      />
      {openAddTipologiaModal && (
        <TipologiaStrumentazioneModal
          description="Nuova tipologia: "
          inputValue={newTipologia}
          onClose={onAddTipologiaModalClose}
          onInputChange={handleInputChangeNewTipologia}
          onSave={handleNewTipologia}
          open={openAddTipologiaModal}
          title="Aggiungi tipologia di strumentazione"
        />
      )}
      {openRemoveTipologiaModal && (
        <TipologiaStrumentazioneModal
          description="Selezionare tipologia da rimuovere: "
          inputValue={selectedTipologiaToRemove || ''}
          onClose={onRemoveTipologiaModalClose}
          onInputChange={handleInputChangeRemoveTipologia}
          onSave={handleRemoveTipologia}
          open={openRemoveTipologiaModal}
          title="Rimuovi tipologia di strumentazione"
          selectOption={tipologiaList}
          isSelect={true}
          selectPlaceholder="Seleziona tipologia"
          warningMessage={removeTipologiaWarning}
          loading={loadingControlliCheck}
        />
      )}
    </>
  );
};

SelezioneAttrezzatureModal.propTypes = {
  gestioneOperativaId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onLocaliRefetch: PropTypes.func,
  selectedLocale: PropTypes.object.isRequired,
  setSelectedLocale: PropTypes.func.isRequired,
};

export default SelezioneAttrezzatureModal;
